'use client';

import { useState, useEffect, useRef } from 'react';

interface ApproachStep {
  id: number;
  number: string;
  title: string;
  description: string;
}

export default function ApproachSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const steps: ApproachStep[] = [
    {
      id: 1,
      number: '01',
      title: 'Discovery & Immersion',
      description: 'Deep dive into your brand essence, target audience psychology, and competitive landscape. Understanding context precedes creation.',
    },
    {
      id: 2,
      number: '02',
      title: 'Strategic Foundation',
      description: 'Develop comprehensive creative strategy, define success metrics, and establish design principles that guide all decisions.',
    },
    {
      id: 3,
      number: '03',
      title: 'Conceptual Exploration',
      description: 'Generate multiple creative directions, explore visual territories, and refine concepts through iterative feedback cycles.',
    },
    {
      id: 4,
      number: '04',
      title: 'Refinement & Execution',
      description: 'Obsessive attention to detail, pixel-perfect implementation, and rigorous quality control across all touchpoints.',
    },
    {
      id: 5,
      number: '05',
      title: 'Launch & Evolution',
      description: 'Strategic rollout, performance monitoring, and continuous optimization based on real-world data and user feedback.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full px-6 lg:px-12 py-32 lg:py-48 bg-background"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          className={`max-w-3xl mb-24 transition-all duration-slow ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-sm font-cta font-semibold tracking-[0.2em] text-muted-foreground uppercase mb-6">
            Approach
          </p>
          <h2 className="text-4xl lg:text-6xl font-headline font-bold leading-[1.1] text-primary mb-8">
            A Methodical Path to Excellence
          </h2>
          <p className="text-xl font-body text-text-secondary leading-relaxed">
            Every project follows a proven framework that balances creative exploration with strategic discipline, ensuring exceptional outcomes.
          </p>
        </div>

        {/* Process Steps */}
        <div className="space-y-0">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`group border-t border-border py-12 transition-all duration-slow ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                {/* Step Number */}
                <div className="lg:col-span-2">
                  <div className="text-6xl lg:text-7xl font-headline font-bold text-primary opacity-20 group-hover:opacity-100 transition-opacity duration-fast">
                    {step.number}
                  </div>
                </div>

                {/* Step Content */}
                <div className="lg:col-span-10 space-y-4">
                  <h3 className="text-2xl lg:text-3xl font-headline font-bold text-primary">
                    {step.title}
                  </h3>
                  <p className="text-lg font-body text-text-secondary leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Border */}
        <div className="border-t border-border mt-0" />
      </div>
    </section>
  );
}